import { Component, Prop, h, Host } from '@stencil/core';
import { NAVBAR_SEO_LINKS } from '../type-1/seo-links';
import { SignNowLogo } from '../shared/sn-logo';

/**
 * Lightweight navbar placeholder for SSR — reserves navbar space and provides SEO-visible navigation
 * links before the full sn-navbar-type-1 loads client-side.
 *
 * @product signnow
 * @version 2.0.0
 * @category SignNow
 * @source navbar
 * @example
 *   <sn-navbar-placeholder-type-1></sn-navbar-placeholder-type-1>
 */
@Component({
  tag: 'sn-navbar-placeholder-type-1',
  styleUrl: 'index.scss',
  shadow: false,
})
export class SnNavbarPlaceholderType1 {
  @Prop() colorMode: 'signnow' | 'inverted' = 'signnow';
  @Prop({ attribute: 'logo-href' }) logoHref: string = '/';
  @Prop() showContactSales: boolean = true;
  @Prop() showFreeTrial: boolean = true;

  /** @content */
  @Prop({ attribute: 'features-label' }) featuresLabel: string = 'Features';
  /** @content */
  @Prop({ attribute: 'solutions-label' }) solutionsLabel: string = 'Solutions';
  /** @content */
  @Prop({ attribute: 'integrations-label' }) integrationsLabel: string = 'Integrations';
  /** @content */
  @Prop({ attribute: 'api-label' }) apiLabel: string = 'API';
  /** @content */
  @Prop({ attribute: 'pricing-label' }) pricingLabel: string = 'Pricing';
  @Prop({ attribute: 'pricing-href' }) pricingHref: string = 'https://snseats.signnow.com/pricing';
  /** @content */
  @Prop({ attribute: 'contact-sales-label' }) contactSalesLabel: string = 'Contact sales';
  @Prop({ attribute: 'contact-sales-href' }) contactSalesHref: string = '/contact';
  /** @content */
  @Prop({ attribute: 'free-trial-label' }) freeTrialLabel: string = 'Free trial';
  @Prop({ attribute: 'free-trial-href' }) freeTrialHref: string = 'https://snseats.signnow.com/purchase/business_free_trial/loggedout';
  /** @content */
  @Prop({ attribute: 'support-label' }) supportLabel: string = 'Support';
  @Prop({ attribute: 'support-href' }) supportHref: string = 'https://support.signnow.com';
  /** @content */
  @Prop({ attribute: 'request-demo-label' }) requestDemoLabel: string = 'Request a demo';
  @Prop({ attribute: 'request-demo-href' }) requestDemoHref: string = '/contact?utm_source=sn-header&utm_medium=contact-sales';

  render() {
    return (
      <Host class="sn-navbar-placeholder" data-color-mode={this.colorMode}>
        {/* SEO links - visually hidden but accessible to crawlers */}
        <nav class="sn-navbar-placeholder__seo-links" aria-label="Site navigation">
          {NAVBAR_SEO_LINKS.map(link => (
            <a href={link.defaultHref}>{link.defaultText}</a>
          ))}
        </nav>

        <navbar-type-1 layout="logo-nav-first" sticky="always" mobileWidth="full" mobileBreakpoint={960} border={false}>
          <div slot="area-logo">
            <a href={this.logoHref} class="me-logo" aria-label="SignNow - Go to homepage">
              <SignNowLogo />
            </a>
          </div>

          {/* Navigation items */}
          <div slot="area-navbar" class="me-dropdown" role="group">
            <me-button tag="span" variant="ghost" icon="arrows--chevron-down" iconPosition="after" class="me-dropdown__trigger">
              {this.featuresLabel}
            </me-button>
          </div>
          <div slot="area-navbar" class="me-dropdown" role="group">
            <me-button tag="span" variant="ghost" icon="arrows--chevron-down" iconPosition="after" class="me-dropdown__trigger">
              {this.solutionsLabel}
            </me-button>
          </div>
          <div slot="area-navbar" class="me-dropdown" role="group">
            <me-button tag="span" variant="ghost" icon="arrows--chevron-down" iconPosition="after" class="me-dropdown__trigger">
              {this.integrationsLabel}
            </me-button>
          </div>
          <div slot="area-navbar" class="me-dropdown" role="group">
            <me-button tag="span" variant="ghost" icon="arrows--chevron-down" iconPosition="after" class="me-dropdown__trigger">
              {this.apiLabel}
            </me-button>
          </div>
          <me-button slot="area-navbar" tag="a" variant="ghost" href={this.pricingHref}>
            {this.pricingLabel}
          </me-button>

          {/* Action buttons */}
          {this.showContactSales && (
            <me-button slot="area-actions" variant="secondary" href={this.contactSalesHref}>
              {this.contactSalesLabel}
            </me-button>
          )}
          {this.showFreeTrial && (
            <me-button slot="area-actions" variant="primary" href={this.freeTrialHref}>
              {this.freeTrialLabel}
            </me-button>
          )}
          <me-button slot="area-actions" variant="ghost" icon="interface--search" iconPosition="only" iconInline={true} aria-label="Search"></me-button>

          {/* Mobile navigation items */}
          <div slot="area-navbar-mobile" class="me-dropdown" role="group">
            <me-button tag="span" variant="ghost" icon="arrows--chevron-down" iconPosition="after" class="me-dropdown__trigger">
              {this.featuresLabel}
            </me-button>
          </div>
          <div slot="area-navbar-mobile" class="me-dropdown" role="group">
            <me-button tag="span" variant="ghost" icon="arrows--chevron-down" iconPosition="after" class="me-dropdown__trigger">
              {this.solutionsLabel}
            </me-button>
          </div>
          <div slot="area-navbar-mobile" class="me-dropdown" role="group">
            <me-button tag="span" variant="ghost" icon="arrows--chevron-down" iconPosition="after" class="me-dropdown__trigger">
              {this.integrationsLabel}
            </me-button>
          </div>
          <div slot="area-navbar-mobile" class="me-dropdown" role="group">
            <me-button tag="span" variant="ghost" icon="arrows--chevron-down" iconPosition="after" class="me-dropdown__trigger">
              {this.apiLabel}
            </me-button>
          </div>
          <me-button slot="area-navbar-mobile" tag="a" variant="ghost" href={this.pricingHref}>
            {this.pricingLabel}
          </me-button>
          <me-button slot="area-navbar-mobile" tag="a" variant="ghost" href={this.supportHref}>
            {this.supportLabel}
          </me-button>

          {/* Mobile action buttons */}
          <me-button slot="area-actions-mobile" variant="secondary" href={this.contactSalesHref}>
            {this.contactSalesLabel}
          </me-button>
          <me-button slot="area-actions-mobile" variant="primary" href={this.requestDemoHref}>
            {this.requestDemoLabel}
          </me-button>
        </navbar-type-1>
      </Host>
    );
  }
}
